import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Play,
  Trash2,
  Download,
  Calendar,
  Youtube,
  Archive,
  Clock,
  Library,
  Grid3x3,
  List,
  FileText,
  X,
  AlertTriangle,
  Music,
  CheckCircle2,
  Volume2,
  Clapperboard,
  IterationCw,
  ImageOff,
  Sparkle,
  AudioLines,
  MicVocal,
} from 'lucide-react';
import type { RenderedVideo } from '@/types/video-enhancer';

const getClipInfo = (video: RenderedVideo): string => {
  const count = video.configuration.clips.length
    || video.shotstackJson?.timeline?.tracks?.reduce(
        (sum: number, t: any) => sum + (t.clips?.length || 0), 0
       ) || 0;
  if (count === 0) return `${Math.round(video.duration)}s`;
  return `${count} clips · ${Math.round(video.duration)}s`;
};

const PipelineStages: React.FC<{ video: RenderedVideo }> = ({ video }) => {
  if (!video.finishingTouches?.enhanceAudio) return null;

  const isRendering = video.status === 'rendering' || video.status === 'processing';
  const isRendered = video.status === 'rendered' || video.status === 'failed';
  const isEnhancingAudio = video.audioEnhancementStatus === 'processing';
  const audioComplete = video.audioEnhancementStatus === 'completed';
  const audioFailed = video.audioEnhancementStatus === 'failed';
  const allDone = isRendered && (audioComplete || audioFailed || (!video.finishingTouches.enhanceAudio));

  const stages = [
    {
      label: 'Render',
      icon: <Clapperboard className="h-3 w-3" />,
      status: isRendered ? 'done' : isRendering ? 'active' : 'pending' as string,
    },
    ...(video.finishingTouches.enhanceAudio ? [{
      label: 'Audio',
      icon: <Volume2 className="h-3 w-3" />,
      status: audioComplete ? 'done' : audioFailed ? 'failed' : isEnhancingAudio ? 'active' : 'pending' as string,
    }] : []),
  ];

  // Only show pipeline if there are finishing touches and we're still in process
  if (allDone && !audioFailed) return null;

  return (
    <div className="mb-2 flex items-center gap-1">
      {stages.map((stage, i) => (
        <React.Fragment key={stage.label}>
          <div className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-medium ${
            stage.status === 'done'
              ? 'bg-success/10 text-success'
              : stage.status === 'active'
              ? 'bg-primary/10 text-primary'
              : stage.status === 'failed'
              ? 'bg-destructive/10 text-destructive'
              : 'bg-secondary text-muted-foreground/50'
          }`}>
            {stage.status === 'active' && (
              <div className="h-2 w-2 animate-pulse rounded-full bg-current" />
            )}
            {stage.status === 'done' && <CheckCircle2 className="h-2.5 w-2.5" />}
            {stage.icon}
            <span>{stage.label}</span>
          </div>
          {i < stages.length - 1 && (
            <div className={`h-px w-3 ${
              stage.status === 'done' ? 'bg-success/50' : 'bg-border'
            }`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// Audio enhancement mode dropdown button
const AudioEnhanceDropdown: React.FC<{
  video: RenderedVideo;
  onEnhanceAudio: (video: RenderedVideo, mode?: 'enhance' | 'denoise' | 'isolate') => void;
  className?: string;
}> = ({ video, onEnhanceAudio, className = 'h-7 w-7' }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button
        variant="ghost"
        size="icon"
        className={className}
        title={video.audioEnhancementStatus === 'failed' ? "Retry Audio Enhancement" : "Enhance Audio"}
      >
        <Music className="h-3.5 w-3.5" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-52">
      <DropdownMenuItem onClick={() => onEnhanceAudio(video, 'enhance')}>
        <Sparkle className="h-3.5 w-3.5 mr-2" />
        Full Enhancement
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => onEnhanceAudio(video, 'denoise')}>
        <AudioLines className="h-3.5 w-3.5 mr-2" />
        Gentle Denoise
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => onEnhanceAudio(video, 'isolate')}>
        <MicVocal className="h-3.5 w-3.5 mr-2" />
        Isolate Speech
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

interface RenderHistoryProps {
  renderedVideos: RenderedVideo[];
  savedRenders: RenderedVideo[];
  youtubeConnected: boolean;
  onViewVideo: (video: RenderedVideo) => void;
  onViewJson: (video: RenderedVideo) => void;
  onDownload: (url: string) => void;
  onSchedule: (video: RenderedVideo) => void;
  onYoutubePublish: (video: RenderedVideo) => void;
  onReimport: (video: RenderedVideo) => void;
  onSaveToLibrary: (video: RenderedVideo) => void;
  onDeleteFromHistory: (videoId: string) => void;
  onDeleteFromLibrary: (videoId: string) => void;
  onRemoveFailedRender: (videoId: string) => void;
  onViewErrorDetails: (video: RenderedVideo) => void;
  onEnhanceAudio: (video: RenderedVideo, mode?: 'enhance' | 'denoise' | 'isolate') => void;
  onRemoveBackground: (video: RenderedVideo) => void;
  onIteratePlan?: (video: RenderedVideo) => void;
}

export const RenderHistory: React.FC<RenderHistoryProps> = ({
  renderedVideos,
  savedRenders,
  youtubeConnected,
  onViewVideo,
  onViewJson,
  onDownload,
  onSchedule,
  onYoutubePublish,
  onReimport,
  onSaveToLibrary,
  onDeleteFromHistory,
  onDeleteFromLibrary,
  onRemoveFailedRender,
  onViewErrorDetails,
  onEnhanceAudio,
  onRemoveBackground,
  onIteratePlan,
}) => {
  const [renderTab, setRenderTab] = useState<'history' | 'library'>('history');
  const [renderViewMode, setRenderViewMode] = useState<'grid' | 'list'>('grid');
  const [playingOriginal, setPlayingOriginal] = useState<Set<string>>(new Set());

  const toggleAudioVersion = (videoId: string) => {
    setPlayingOriginal(prev => {
      const next = new Set(prev);
      if (next.has(videoId)) {
        next.delete(videoId);
      } else {
        next.add(videoId);
      }
      return next;
    });
  };

  const getPlaybackVideo = (video: RenderedVideo): RenderedVideo => {
    if (playingOriginal.has(video.id) && video.downloadUrl) {
      return { ...video, url: video.downloadUrl };
    }
    return video;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Renders</h2>
          {/* Tab Buttons */}
          <div className="flex rounded-lg border border-border bg-secondary/30">
            <Button
              variant="ghost"
              size="sm"
              className={`h-7 gap-1.5 rounded-r-none px-3 text-xs ${
                renderTab === 'history' ? 'bg-secondary text-foreground' : 'text-muted-foreground'
              }`}
              onClick={() => setRenderTab('history')}
            >
              <Clock className="h-3 w-3" />
              History
              {renderedVideos.length > 0 && (
                <span className="rounded bg-muted px-1.5 text-[10px]">{renderedVideos.length}</span>
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={`h-7 gap-1.5 rounded-l-none px-3 text-xs ${
                renderTab === 'library' ? 'bg-secondary text-foreground' : 'text-muted-foreground'
              }`}
              onClick={() => setRenderTab('library')}
            >
              <Library className="h-3 w-3" />
              Library
              {savedRenders.length > 0 && (
                <span className="rounded bg-muted px-1.5 text-[10px]">{savedRenders.length}</span>
              )}
            </Button>
          </div>
        </div>
        {/* View Mode */}
        <div className="flex rounded-lg border border-border bg-secondary/30">
          <Button
            variant="ghost"
            size="icon"
            className={`h-7 w-7 rounded-r-none ${
              renderViewMode === 'grid' ? 'bg-secondary text-foreground' : 'text-muted-foreground'
            }`}
            onClick={() => setRenderViewMode('grid')}
          >
            <Grid3x3 className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className={`h-7 w-7 rounded-l-none ${
              renderViewMode === 'list' ? 'bg-secondary text-foreground' : 'text-muted-foreground'
            }`}
            onClick={() => setRenderViewMode('list')}
          >
            <List className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* History Tab */}
      {renderTab === 'history' && (
        <>
          {/* Warning Banner */}
          {renderedVideos.some(v => v.status === 'rendered') && (
            <div className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 p-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning" />
              <p className="text-xs text-warning">
                <strong>Renders expire after 24 hours.</strong> Click "Save to Library" to keep your videos permanently.
              </p>
            </div>
          )}

          {renderedVideos.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Clock className="mb-3 h-12 w-12 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">No recent renders</p>
              <p className="mt-1 text-xs text-muted-foreground/70">Rendered videos will appear here</p>
            </div>
          ) : (
            <div className={renderViewMode === 'grid' ? 'grid gap-3 md:grid-cols-2 lg:grid-cols-3' : 'space-y-2'}>
              {renderedVideos.map(video => {
                const isRendering = video.status === 'rendering' || video.status === 'processing';
                const isRendered = video.status === 'rendered';
                const isFailed = video.status === 'failed';

                if (renderViewMode === 'list') {
                  return (
                    <Card key={video.id} className="border-border bg-[hsl(var(--panel))] p-3">
                      <div className="flex items-center gap-4">
                        {/* Thumbnail */}
                        <div
                          className="group relative h-14 w-24 flex-shrink-0 cursor-pointer overflow-hidden rounded bg-[hsl(var(--timeline))]"
                          onClick={() => isRendered && video.url && onViewVideo(getPlaybackVideo(video))}
                        >
                          {video.thumbnailUrl ? (
                            <img src={video.thumbnailUrl} alt={video.name} className="h-full w-full object-cover" />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center">
                              <Play className="h-6 w-6 text-muted-foreground" />
                            </div>
                          )}
                          {isRendered && video.url && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition-opacity group-hover:opacity-100">
                              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/90">
                                <Play className="ml-0.5 h-4 w-4 text-black" />
                              </div>
                            </div>
                          )}
                          {video.audioEnhancementStatus === 'completed' && video.downloadUrl && (
                            <div
                              className={`absolute top-1 right-1 rounded px-1.5 py-0.5 text-[9px] font-medium text-white flex items-center gap-0.5 cursor-pointer ${
                                playingOriginal.has(video.id) ? 'bg-muted-foreground/80' : 'bg-green-500/90'
                              }`}
                              onClick={(e) => { e.stopPropagation(); toggleAudioVersion(video.id); }}
                              title="Click to toggle between original and enhanced audio"
                            >
                              {playingOriginal.has(video.id) ? (
                                <>
                                  <Music className="h-2.5 w-2.5" />
                                  <span>Original</span>
                                </>
                              ) : (
                                <>
                                  <CheckCircle2 className="h-2.5 w-2.5" />
                                  <span>Enhanced</span>
                                </>
                              )}
                            </div>
                          )}
                          {video.audioEnhancementStatus === 'completed' && !video.downloadUrl && (
                            <div className="absolute top-1 right-1 rounded bg-green-500/90 px-1.5 py-0.5 text-[9px] font-medium text-white flex items-center gap-0.5">
                              <CheckCircle2 className="h-2.5 w-2.5" />
                              <span>Enhanced</span>
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            {isRendering && (
                              <div className="h-2 w-2 animate-pulse rounded-full bg-primary" />
                            )}
                            {isRendered && <div className="h-2 w-2 rounded-full bg-success" />}
                            {isFailed && <div className="h-2 w-2 rounded-full bg-destructive" />}
                            <h3 className="truncate text-xs font-medium">{video.name}</h3>
                          </div>
                          <p className="text-[10px] text-muted-foreground">
                            {getClipInfo(video)} · {new Date(video.timestamp).toLocaleString()}
                          </p>
                          {isRendering && video.progress !== undefined && (
                            <div className="mt-1 h-1 w-32 overflow-hidden rounded-full bg-secondary">
                              <div className="h-full bg-primary transition-all" style={{ width: `${video.progress}%` }} />
                            </div>
                          )}
                          {video.audioEnhancementStatus === 'processing' && (
                            <div className="mt-1 flex items-center gap-2">
                              <div className="h-1 w-32 overflow-hidden rounded-full bg-secondary">
                                <div
                                  className="h-full bg-primary transition-all duration-300"
                                  style={{ width: `${video.audioEnhancementProgress || 0}%` }}
                                />
                              </div>
                              <span className="text-[10px] text-muted-foreground">Enhancing audio...</span>
                            </div>
                          )}
                          {video.audioEnhancementStatus === 'failed' && (
                            <p className="mt-1 text-[10px] text-destructive">Audio enhancement failed</p>
                          )}
                          {video.bgRemovalStatus === 'processing' && (
                            <div className="mt-1 flex items-center gap-2">
                              <div className="h-1 w-32 overflow-hidden rounded-full bg-secondary">
                                <div
                                  className="h-full bg-primary transition-all duration-300"
                                  style={{ width: `${video.bgRemovalProgress || 0}%` }}
                                />
                              </div>
                              <span className="text-[10px] text-muted-foreground">Removing background...</span>
                            </div>
                          )}
                          {video.bgCompositeStatus === 'processing' && (
                            <div className="mt-1 flex items-center gap-2">
                              <div className="h-1 w-32 animate-pulse overflow-hidden rounded-full bg-secondary">
                                <div className="h-full w-2/3 bg-primary" />
                              </div>
                              <span className="text-[10px] text-muted-foreground">Compositing...</span>
                            </div>
                          )}
                          <PipelineStages video={video} />
                        </div>

                        {/* Actions */}
                        <div className="flex flex-shrink-0 gap-1.5">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onViewJson(video)}>
                            <FileText className="h-3.5 w-3.5" />
                          </Button>
                          {isRendered && video.url && (
                            <>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onDownload(video.url!)}>
                                <Download className="h-3.5 w-3.5" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onSchedule(video)}>
                                <Calendar className="h-3.5 w-3.5" />
                              </Button>
                              {youtubeConnected && (
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => onYoutubePublish(video)}>
                                  <Youtube className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              {(!video.audioEnhancementStatus || video.audioEnhancementStatus === 'failed') && (
                                <AudioEnhanceDropdown video={video} onEnhanceAudio={onEnhanceAudio} />
                              )}
                              {(!video.bgRemovalStatus || video.bgRemovalStatus === 'failed') && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => onRemoveBackground(video)}
                                  title={video.bgRemovalStatus === 'failed' ? "Retry Background Removal" : "Remove Background"}
                                >
                                  <ImageOff className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-success" onClick={() => onSaveToLibrary(video)}>
                                <Archive className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          )}
                          {video.aiCreativePlan && onIteratePlan && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-accent"
                              onClick={() => onIteratePlan(video)}
                              title="Iterate on this plan"
                            >
                              <IterationCw className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" onClick={() => onDeleteFromHistory(video.id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  );
                }

                // Grid view
                return (
                  <Card key={video.id} className="border-border bg-[hsl(var(--panel))] p-3">
                    {/* Status */}
                    <div className="mb-2 flex items-center justify-between">
                      {isRendering && (
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-2 animate-pulse rounded-full bg-primary" />
                          <span className="text-[10px] font-medium text-primary">Rendering...</span>
                        </div>
                      )}
                      {isRendered && (
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-success" />
                          <span className="text-[10px] font-medium text-success">Rendered</span>
                        </div>
                      )}
                      {isFailed && (
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-destructive" />
                          <span className="text-[10px] font-medium text-destructive">Failed</span>
                        </div>
                      )}
                      <span className="text-[10px] text-muted-foreground">
                        {new Date(video.timestamp).toLocaleTimeString()}
                      </span>
                    </div>

                    {/* Progress */}
                    {isRendering && video.progress !== undefined && (
                      <div className="mb-2 h-1 overflow-hidden rounded-full bg-secondary">
                        <div className="h-full bg-primary transition-all" style={{ width: `${video.progress}%` }} />
                      </div>
                    )}
                    {video.audioEnhancementStatus === 'processing' && (
                      <div className="mb-2">
                        <div className="h-1 overflow-hidden rounded-full bg-secondary">
                          <div
                            className="h-full bg-primary transition-all duration-300"
                            style={{ width: `${video.audioEnhancementProgress || 0}%` }}
                          />
                        </div>
                        <p className="mt-1 text-[10px] text-muted-foreground">Enhancing audio...</p>
                      </div>
                    )}
                    {video.audioEnhancementStatus === 'failed' && (
                      <div className="mb-2">
                        <p className="text-[10px] text-destructive">Audio enhancement failed — click the music icon to retry</p>
                      </div>
                    )}
                    {video.bgRemovalStatus === 'processing' && (
                      <div className="mb-2">
                        <div className="h-1 overflow-hidden rounded-full bg-secondary">
                          <div
                            className="h-full bg-primary transition-all duration-300"
                            style={{ width: `${video.bgRemovalProgress || 0}%` }}
                          />
                        </div>
                        <p className="mt-1 text-[10px] text-muted-foreground">Removing background...</p>
                      </div>
                    )}
                    {video.bgCompositeStatus === 'processing' && (
                      <div className="mb-2">
                        <div className="h-1 animate-pulse overflow-hidden rounded-full bg-secondary">
                          <div className="h-full w-2/3 bg-primary" />
                        </div>
                        <p className="mt-1 text-[10px] text-muted-foreground">Compositing background...</p>
                      </div>
                    )}

                    {/* Pipeline Stages */}
                    <PipelineStages video={video} />

                    {/* Thumbnail */}
                    <div
                      className="group relative mb-3 aspect-video cursor-pointer overflow-hidden rounded-lg bg-[hsl(var(--timeline))]"
                      onClick={() => isRendered && video.url && onViewVideo(getPlaybackVideo(video))}
                    >
                      {isRendered && video.url ? (
                        <>
                          {video.thumbnailUrl ? (
                            <img src={video.thumbnailUrl} alt={video.name} className="h-full w-full object-cover" />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center">
                              <Play className="h-12 w-12 text-muted-foreground/50" />
                            </div>
                          )}
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition-opacity group-hover:opacity-100">
                            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 transition-transform group-hover:scale-110">
                              <Play className="ml-1 h-7 w-7 text-black" />
                            </div>
                          </div>
                          {video.audioEnhancementStatus === 'completed' && video.downloadUrl && (
                            <div
                              className={`absolute top-2 right-2 rounded px-2 py-0.5 text-[10px] font-medium text-white flex items-center gap-1 cursor-pointer transition-colors ${
                                playingOriginal.has(video.id) ? 'bg-muted-foreground/80' : 'bg-green-500/90'
                              }`}
                              onClick={(e) => { e.stopPropagation(); toggleAudioVersion(video.id); }}
                              title="Click to toggle between original and enhanced audio"
                            >
                              {playingOriginal.has(video.id) ? (
                                <>
                                  <Music className="h-3 w-3" />
                                  <span>Original</span>
                                </>
                              ) : (
                                <>
                                  <CheckCircle2 className="h-3 w-3" />
                                  <span>Enhanced</span>
                                </>
                              )}
                            </div>
                          )}
                          {video.audioEnhancementStatus === 'completed' && !video.downloadUrl && (
                            <div className="absolute top-2 right-2 rounded bg-green-500/90 px-2 py-0.5 text-[10px] font-medium text-white flex items-center gap-1">
                              <CheckCircle2 className="h-3 w-3" />
                              <span>Enhanced</span>
                            </div>
                          )}
                          {(video.bgRemovalStatus === 'completed' || video.bgReplacedUrl) && (
                            <div className={`absolute ${video.audioEnhancementStatus === 'completed' ? 'top-8' : 'top-2'} left-2 rounded px-2 py-0.5 text-[10px] font-medium text-white flex items-center gap-1 ${video.bgReplacedUrl ? 'bg-purple-500/90' : 'bg-blue-500/90'}`}>
                              <ImageOff className="h-3 w-3" />
                              <span>{video.bgReplacedUrl ? 'BG Replaced' : 'BG Removed'}</span>
                            </div>
                          )}
                        </>
                      ) : video.thumbnailUrl ? (
                        <img src={video.thumbnailUrl} alt={video.name} className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full flex-col items-center justify-center">
                          {isRendering ? (
                            <>
                              <Play className="mb-2 h-10 w-10 animate-pulse text-muted-foreground/50" />
                              {video.progress !== undefined && (
                                <span className="text-sm font-medium">{video.progress}%</span>
                              )}
                            </>
                          ) : (
                            <Play className="h-10 w-10 text-muted-foreground/50" />
                          )}
                        </div>
                      )}
                      {isFailed && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-destructive/10">
                          <X className="mb-2 h-8 w-8 text-destructive" />
                          <p className="text-xs font-medium text-destructive">Render Failed</p>
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="mb-3">
                      <h3 className="truncate text-xs font-medium">{video.name}</h3>
                      <div className="mt-1 flex items-center justify-between">
                        <span className="text-[10px] text-muted-foreground">
                          {getClipInfo(video)}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {new Date(video.timestamp).toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-1.5">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 flex-shrink-0 text-muted-foreground"
                        onClick={() => onDeleteFromHistory(video.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                      {isRendered && video.url && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 flex-shrink-0"
                            onClick={() => onDownload(video.url!)}
                          >
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 flex-shrink-0 text-xs"
                            onClick={() => onReimport(video)}
                          >
                            Re-use
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 flex-shrink-0"
                            onClick={() => onSchedule(video)}
                          >
                            <Calendar className="h-3.5 w-3.5" />
                          </Button>
                          {youtubeConnected && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 flex-shrink-0 text-destructive"
                              onClick={() => onYoutubePublish(video)}
                            >
                              <Youtube className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {(!video.audioEnhancementStatus || video.audioEnhancementStatus === 'failed') && (
                            <AudioEnhanceDropdown video={video} onEnhanceAudio={onEnhanceAudio} className="h-7 w-7 flex-shrink-0" />
                          )}
                          {(!video.bgRemovalStatus || video.bgRemovalStatus === 'failed') && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 flex-shrink-0"
                              onClick={() => onRemoveBackground(video)}
                              title={video.bgRemovalStatus === 'failed' ? "Retry Background Removal" : "Remove Background"}
                            >
                              <ImageOff className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 flex-shrink-0 text-success"
                            onClick={() => onSaveToLibrary(video)}
                          >
                            <Archive className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                      {video.aiCreativePlan && onIteratePlan && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 flex-shrink-0 gap-1 text-xs text-accent"
                          onClick={() => onIteratePlan(video)}
                          title="Iterate on this plan"
                        >
                          <IterationCw className="h-3.5 w-3.5" />
                          Iterate
                        </Button>
                      )}
                      {isRendering && (
                        <Button variant="ghost" size="sm" className="h-7 flex-1 text-xs" disabled>
                          <div className="mr-2 h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                          Rendering...
                        </Button>
                      )}
                      {isFailed && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 flex-1 text-xs"
                            onClick={() => onViewErrorDetails(video)}
                          >
                            <FileText className="mr-1 h-3.5 w-3.5" />
                            Details
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 flex-1 text-xs text-destructive"
                            onClick={() => onRemoveFailedRender(video.id)}
                          >
                            <X className="mr-1 h-3.5 w-3.5" />
                            Remove
                          </Button>
                        </>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Library Tab */}
      {renderTab === 'library' && (
        <>
          {savedRenders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Library className="mb-3 h-12 w-12 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">No saved renders</p>
              <p className="mt-1 text-xs text-muted-foreground/70">Save renders from History to keep them permanently</p>
            </div>
          ) : (
            <div className={renderViewMode === 'grid' ? 'grid gap-3 md:grid-cols-2 lg:grid-cols-3' : 'space-y-2'}>
              {savedRenders.map(video => {
                const videoUrl = video.savedS3Url || video.url;

                if (renderViewMode === 'list') {
                  return (
                    <Card key={video.id} className="border-border bg-[hsl(var(--panel))] p-3">
                      <div className="flex items-center gap-4">
                        <div
                          className="group relative h-14 w-24 flex-shrink-0 cursor-pointer overflow-hidden rounded bg-[hsl(var(--timeline))]"
                          onClick={() => {
                            const playUrl = playingOriginal.has(video.id) && video.downloadUrl ? video.downloadUrl : videoUrl;
                            playUrl && onViewVideo({ ...video, url: playUrl });
                          }}
                        >
                          {video.thumbnailUrl ? (
                            <img src={video.thumbnailUrl} alt={video.name} className="h-full w-full object-cover" />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center">
                              <Play className="h-6 w-6 text-muted-foreground" />
                            </div>
                          )}
                          {videoUrl && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition-opacity group-hover:opacity-100">
                              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/90">
                                <Play className="ml-0.5 h-4 w-4 text-black" />
                              </div>
                            </div>
                          )}
                          {video.audioEnhancementStatus === 'completed' && video.downloadUrl && (
                            <div
                              className={`absolute top-1 right-1 rounded px-1.5 py-0.5 text-[9px] font-medium text-white flex items-center gap-0.5 cursor-pointer ${
                                playingOriginal.has(video.id) ? 'bg-muted-foreground/80' : 'bg-green-500/90'
                              }`}
                              onClick={(e) => { e.stopPropagation(); toggleAudioVersion(video.id); }}
                              title="Click to toggle between original and enhanced audio"
                            >
                              {playingOriginal.has(video.id) ? (
                                <>
                                  <Music className="h-2.5 w-2.5" />
                                  <span>Original</span>
                                </>
                              ) : (
                                <>
                                  <CheckCircle2 className="h-2.5 w-2.5" />
                                  <span>Enhanced</span>
                                </>
                              )}
                            </div>
                          )}
                          {video.audioEnhancementStatus === 'completed' && !video.downloadUrl && (
                            <div className="absolute top-1 right-1 rounded bg-green-500/90 px-1.5 py-0.5 text-[9px] font-medium text-white flex items-center gap-0.5">
                              <CheckCircle2 className="h-2.5 w-2.5" />
                              <span>Enhanced</span>
                            </div>
                          )}
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <div className="h-2 w-2 rounded-full bg-success" />
                            <h3 className="truncate text-xs font-medium">{video.name}</h3>
                          </div>
                          <p className="text-[10px] text-muted-foreground">
                            {getClipInfo(video)} · Saved {new Date(video.savedAt!).toLocaleDateString()}
                          </p>
                          {video.audioEnhancementStatus === 'processing' && (
                            <div className="mt-1 flex items-center gap-2">
                              <div className="h-1 w-32 overflow-hidden rounded-full bg-secondary">
                                <div
                                  className="h-full bg-primary transition-all duration-300"
                                  style={{ width: `${video.audioEnhancementProgress || 0}%` }}
                                />
                              </div>
                              <span className="text-[10px] text-muted-foreground">Enhancing audio...</span>
                            </div>
                          )}
                          {video.audioEnhancementStatus === 'failed' && (
                            <p className="mt-1 text-[10px] text-destructive">Audio enhancement failed</p>
                          )}
                        </div>

                        <div className="flex flex-shrink-0 gap-1.5">
                          {videoUrl && (
                            <>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => window.open(videoUrl, '_blank')}>
                                <Download className="h-3.5 w-3.5" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onSchedule({ ...video, url: videoUrl })}>
                                <Calendar className="h-3.5 w-3.5" />
                              </Button>
                              {(!video.audioEnhancementStatus || video.audioEnhancementStatus === 'failed') && (
                                <AudioEnhanceDropdown video={{ ...video, url: videoUrl }} onEnhanceAudio={onEnhanceAudio} />
                              )}
                              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => onReimport({ ...video, url: videoUrl })}>
                                Re-use
                              </Button>
                            </>
                          )}
                          {video.aiCreativePlan && onIteratePlan && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-accent"
                              onClick={() => onIteratePlan(video)}
                              title="Iterate on this plan"
                            >
                              <IterationCw className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" onClick={() => onDeleteFromLibrary(video.id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  );
                }

                // Grid view
                return (
                  <Card key={video.id} className="border-border bg-[hsl(var(--panel))] p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full bg-success" />
                        <span className="text-[10px] font-medium text-success">Saved</span>
                      </div>
                      <span className="text-[10px] text-muted-foreground">
                        {new Date(video.savedAt!).toLocaleDateString()}
                      </span>
                    </div>

                    {video.audioEnhancementStatus === 'processing' && (
                      <div className="mb-2">
                        <div className="h-1 overflow-hidden rounded-full bg-secondary">
                          <div
                            className="h-full bg-primary transition-all duration-300"
                            style={{ width: `${video.audioEnhancementProgress || 0}%` }}
                          />
                        </div>
                        <p className="mt-1 text-[10px] text-muted-foreground">Enhancing audio...</p>
                      </div>
                    )}
                    {video.audioEnhancementStatus === 'failed' && (
                      <div className="mb-2">
                        <p className="text-[10px] text-destructive">Audio enhancement failed — click the music icon to retry</p>
                      </div>
                    )}

                    <div
                      className="group relative mb-3 aspect-video cursor-pointer overflow-hidden rounded-lg bg-[hsl(var(--timeline))]"
                      onClick={() => {
                        const playUrl = playingOriginal.has(video.id) && video.downloadUrl ? video.downloadUrl : videoUrl;
                        playUrl && onViewVideo({ ...video, url: playUrl });
                      }}
                    >
                      {video.thumbnailUrl ? (
                        <>
                          <img src={video.thumbnailUrl} alt={video.name} className="h-full w-full object-cover" />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition-opacity group-hover:opacity-100">
                            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 transition-transform group-hover:scale-110">
                              <Play className="ml-1 h-7 w-7 text-black" />
                            </div>
                          </div>
                          {video.audioEnhancementStatus === 'completed' && video.downloadUrl && (
                            <div
                              className={`absolute top-2 right-2 rounded px-2 py-0.5 text-[10px] font-medium text-white flex items-center gap-1 cursor-pointer transition-colors ${
                                playingOriginal.has(video.id) ? 'bg-muted-foreground/80' : 'bg-green-500/90'
                              }`}
                              onClick={(e) => { e.stopPropagation(); toggleAudioVersion(video.id); }}
                              title="Click to toggle between original and enhanced audio"
                            >
                              {playingOriginal.has(video.id) ? (
                                <>
                                  <Music className="h-3 w-3" />
                                  <span>Original</span>
                                </>
                              ) : (
                                <>
                                  <CheckCircle2 className="h-3 w-3" />
                                  <span>Enhanced</span>
                                </>
                              )}
                            </div>
                          )}
                          {video.audioEnhancementStatus === 'completed' && !video.downloadUrl && (
                            <div className="absolute top-2 right-2 rounded bg-green-500/90 px-2 py-0.5 text-[10px] font-medium text-white flex items-center gap-1">
                              <CheckCircle2 className="h-3 w-3" />
                              <span>Enhanced</span>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="flex h-full w-full items-center justify-center">
                          <Play className="h-10 w-10 text-muted-foreground/50" />
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <h3 className="truncate text-xs font-medium">{video.name}</h3>
                      <div className="mt-1 flex items-center justify-between">
                        <span className="text-[10px] text-muted-foreground">
                          {getClipInfo(video)}
                        </span>
                      </div>
                    </div>

                    <div className="flex gap-1.5">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 flex-shrink-0 text-muted-foreground"
                        onClick={() => onDeleteFromLibrary(video.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                      {videoUrl && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 flex-shrink-0"
                            onClick={() => window.open(videoUrl, '_blank')}
                          >
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 flex-shrink-0 text-xs"
                            onClick={() => onReimport({ ...video, url: videoUrl })}
                          >
                            Re-use
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 flex-shrink-0"
                            onClick={() => onSchedule({ ...video, url: videoUrl })}
                          >
                            <Calendar className="h-3.5 w-3.5" />
                          </Button>
                          {(!video.audioEnhancementStatus || video.audioEnhancementStatus === 'failed') && (
                            <AudioEnhanceDropdown video={{ ...video, url: videoUrl }} onEnhanceAudio={onEnhanceAudio} className="h-7 w-7 flex-shrink-0" />
                          )}
                        </>
                      )}
                      {video.aiCreativePlan && onIteratePlan && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 flex-shrink-0 gap-1 text-xs text-accent"
                          onClick={() => onIteratePlan(video)}
                          title="Iterate on this plan"
                        >
                          <IterationCw className="h-3.5 w-3.5" />
                          Iterate
                        </Button>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};
